import classNames from "classnames";
import { forwardRef, useRef, useState } from "react";
import { Transition } from "@headlessui/react";
import { usePopper } from "react-popper";
import { twMerge } from "tailwind-merge";
import Portal from "@/components/UI/Portal/Portal";
import TextField, { TextFieldProps } from "@/components/UI/TextField/TextField";
import { HexColorPicker } from "react-colorful";
import useHandleClickOutside from "@/hooks/useHandleClickOutside";

export type ColorPickerTextFieldRootNode = HTMLElement;

export interface ColorPickerTextFieldProps extends TextFieldProps {
  onColorPickerChange?: (value: string) => void;
}

const ColorPickerTextField = forwardRef<ColorPickerTextFieldRootNode, ColorPickerTextFieldProps>(
  ({ color, onColorPickerChange, value, ...props }, _ref) => {
    const popperRef = useRef(null);

    const [open, setOpen] = useState(false);

    const [popperElement, setPopperElement] = useState(null);
    const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);

    const popper = usePopper(targetElement, popperElement, {
      modifiers: [
        {
          name: "preventOverflow",
          options: {
            padding: 10,
          },
        },
      ],
      placement: "bottom",
      strategy: "fixed",
    });

    useHandleClickOutside({
      outsideClickcallback: () => {
        setOpen(false);
      },
      ref: popperRef,
    });

    return (
      <div className="relative">
        <div className={twMerge(classNames("relative"))} onClick={() => setOpen(true)} ref={setTargetElement}>
          <TextField
            slots={{
              input: {
                css: "pl-12",
              },
            }}
            startNode={<div className="h-7 w-7 rounded border" style={{ background: value }} />}
            value={value}
            {...props}
          />
        </div>
        <Portal>
          <div
            className="z-50"
            ref={popperRef}
            style={{
              ...popper.styles.popper,
              minWidth: targetElement?.scrollWidth,
            }}
            {...popper.attributes.popper}
          >
            <Transition
              beforeEnter={() => setPopperElement(popperRef.current)}
              afterLeave={() => setPopperElement(null)}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
              show={open}
              as={"div"}
            >
              <HexColorPicker
                className="mt-1 !w-full"
                color={value}
                onChange={(value = "#1882ff") => {
                  onColorPickerChange?.(value);
                }}
              />
            </Transition>
          </div>
        </Portal>
      </div>
    );
  }
);

ColorPickerTextField.displayName = ColorPickerTextField.name;

export default ColorPickerTextField;
